import {
  AddressCard,
  Button,
  Check,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useFormDataContext } from "@totalenergiescode/mobility-business-rev-form-data/components";
import React from "react";

import EditableTextField from "./EditableTextField";
import {
  StyledActions,
  StyledBlock,
  StyledContainer,
  StyledDrawer,
  StyledFormContainer,
  StyledText,
} from "./styles";

interface EditableCardProps {
  fieldId: string;
  titleAddressTranslation: string;
  titlePinTranslation: string;
  cancelButtonTranslation: string;
  submitButtonTranslation: string;
  nameLabelTranslation: string;
  addressLabelTranslation: string;
}

interface EditableCardFormValues {
  name: string;
  companyName: string;
  address: string;
  location: string;
  city: string;
  zipCode: string;
  country: string;
}

function EditableCard({
  fieldId,
  titleAddressTranslation,
  titlePinTranslation,
  cancelButtonTranslation,
  submitButtonTranslation,
  nameLabelTranslation,
  addressLabelTranslation,
}: Readonly<EditableCardProps>) {
  const editableCardSnapshotRef = React.useRef<EditableCardFormValues | null>();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const {
    dataTestIdPrefix,
    findFieldById,
    findCompositeFieldByName,
    translatorFor,
  } = useFormDataContext();

  const field = findFieldById(fieldId);
  const nameValue = field.value.asString();

  const companyNameField = findCompositeFieldByName(field, "companyName");
  const companyNameValue = companyNameField?.value.asString();

  const addressField = findCompositeFieldByName(field, "address");
  const addressValue = addressField?.value.asString();

  const locationField = findCompositeFieldByName(field, "location");
  const locationValue = locationField?.value.asString();

  const cityField = findCompositeFieldByName(field, "city");
  const cityValue = cityField?.value.asString();

  const zipCodeField = findCompositeFieldByName(field, "zipCode");
  const zipCodeValue = zipCodeField?.value.asString();

  const countryField = findCompositeFieldByName(field, "country");
  const countryValue = countryField?.value.asString();

  const data: EditableCardFormValues = {
    name: nameValue.value() || nameValue.defaultValue(),
    companyName: companyNameValue.value() || companyNameValue.defaultValue(),
    address: addressValue.value() || addressValue.defaultValue(),
    city: cityValue.value() || cityValue.defaultValue(),
    zipCode: zipCodeValue.value() || zipCodeValue.defaultValue(),
    country: countryValue.value() || countryValue.defaultValue(),
    location: locationValue.value() || locationValue.defaultValue(),
  };

  const isDeliveryPin = fieldId.includes("pinDeliveryAddress");
  const isEditable = field.writable;

  const translator = translatorFor(field);
  const errorMessage =
    field.violations().length > 0
      ? field.violations().at(0)?.translate(translator)
      : undefined;

  function resetEditableCard() {
    const snapshot = editableCardSnapshotRef.current;

    if (snapshot) {
      field.setValue(snapshot.name);
      companyNameField.setValue(snapshot.companyName);
      addressField.setValue(snapshot.address);
      cityField.setValue(snapshot.city);
      zipCodeField.setValue(snapshot.zipCode);
      countryField.setValue(snapshot.country);
      locationField.setValue(snapshot.location);
    }

    editableCardSnapshotRef.current = null;
  }

  function handleReset() {
    resetEditableCard();

    setIsDrawerOpen(false);
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setIsDrawerOpen(false);
  }

  function handleOpenDrawer() {
    editableCardSnapshotRef.current = data;

    setIsDrawerOpen(true);
  }

  return (
    <div>
      <AddressCard
        nameLabel={nameLabelTranslation}
        addressLabel={addressLabelTranslation}
        address={data}
        isEditable={isEditable}
        onClick={handleOpenDrawer}
        dataTestId={`${dataTestIdPrefix}-editable-card`}
        errorMessage={errorMessage || undefined}
      />

      <StyledDrawer isOpen={isDrawerOpen} onClose={handleReset}>
        <StyledContainer>
          <StyledBlock>
            <StyledText variant='title4'>
              {isDeliveryPin ? titlePinTranslation : titleAddressTranslation}
            </StyledText>
          </StyledBlock>
          <form onSubmit={handleSubmit} noValidate>
            <StyledFormContainer>
              <StyledBlock>
                <EditableTextField field={field} />
              </StyledBlock>
              <StyledBlock>
                <EditableTextField field={companyNameField} />
              </StyledBlock>
              <StyledBlock>
                <EditableTextField field={addressField} />
              </StyledBlock>
              <StyledBlock>
                <EditableTextField field={locationField} />
              </StyledBlock>
              <StyledBlock isRow>
                <EditableTextField field={zipCodeField} />
                <EditableTextField field={cityField} />
              </StyledBlock>
              <StyledBlock>
                <EditableTextField field={countryField} />
              </StyledBlock>
              <StyledActions>
                <Button
                  type='button'
                  variant='secondary'
                  buttonText={cancelButtonTranslation}
                  onClick={handleReset}
                  isLowercase
                  dataTestId={`${dataTestIdPrefix}-cancel`}
                />
                <Button
                  type='submit'
                  variant='primary'
                  buttonText={submitButtonTranslation}
                  leftIcon={<Check color='white' />}
                  isLowercase
                  dataTestId={`${dataTestIdPrefix}-confirm`}
                />
              </StyledActions>
            </StyledFormContainer>
          </form>
        </StyledContainer>
      </StyledDrawer>
    </div>
  );
}

export default EditableCard;
